import { QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider, type RouterHistory } from '@tanstack/react-router'

import { authKeys } from '@/shared/auth/session'
import { TenantPalette } from '@/shared/theme/tenant-palette'
import { ThemeProvider } from '@/shared/theme/theme-provider'
import { TooltipProvider } from '@/shared/ui/tooltip'

import { createQueryClient } from './query-client'
import { createAppRouter, type AppRouter } from './router'

/**
 * Monta QueryClient + Router. Usado pelo main.tsx e pelos testes (com memory history).
 */
/** Telas acessíveis sem sessão: um 401 nelas não leva de volta ao login. */
const PUBLIC_PATHS = ['/login', '/forgot-password', '/reset-password']

export function createApp(history?: RouterHistory) {
  // eslint-disable-next-line prefer-const -- atribuído logo abaixo; o callback precisa da referência.
  let router: AppRouter

  const queryClient = createQueryClient({
    onUnauthenticated: (queryKey) => {
      // O 401 do próprio /me é tratado pelo beforeLoad da rota autenticada.
      if (queryKey?.[0] === authKeys.me[0] && queryKey[1] === authKeys.me[1]) return
      if (PUBLIC_PATHS.includes(router.state.location.pathname)) return

      queryClient.removeQueries({ queryKey: authKeys.me })
      void router.navigate({ to: '/login', search: { redirect: router.state.location.href, expired: true } })
    },
  })

  router = createAppRouter(queryClient, history)

  function App() {
    return (
      <ThemeProvider>
        <QueryClientProvider client={queryClient}>
          <TenantPalette />
          <TooltipProvider>
            <RouterProvider router={router} />
          </TooltipProvider>
        </QueryClientProvider>
      </ThemeProvider>
    )
  }

  return { App, router, queryClient }
}
